import { useEffect, useState } from 'react';
import { requestOtp, verifyOtp } from '../apis/auth';
import { usePendingAuthStore } from '../store/pendingAuthStore';
import type { OtpRequest } from '../types/auth.types';
import { apiErrorBody, apiErrorMessage } from '../utils/apiError';
import useTurnstile from './useTurnstile';

/**
 * Sends the WhatsApp (or SMS) code through api-dsh and checks it. Render an
 * element with `turnstileRef`: every send first passes a Turnstile check.
 * The last code sent (number, channel, resend time) is kept in usePendingAuthStore,
 * so the countdown carries on from one screen to the next.
 */
const usePhoneVerification = () => {
  const sent = usePendingAuthStore((state) => state.sent);
  const { containerRef: turnstileRef, getToken } = useTurnstile();
  const [now, setNow] = useState(() => Date.now());
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');

  const resendIn = sent ? Math.max(0, Math.ceil((sent.resendAt - now) / 1000)) : 0;

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setNow(Date.now()), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  /** Sends a code; throws the request error so the caller can show it where it belongs. */
  const send = async (body: OtpRequest) => {
    setIsSending(true);
    setError('');
    const store = usePendingAuthStore.getState();
    try {
      const turnstileToken = await getToken();
      const response = await requestOtp({ ...body, turnstile_token: turnstileToken });
      store.setSent({
        purpose: body.purpose,
        phone: response.phone_number,
        channel: response.channel,
        resendAt: Date.now() + response.resend_in * 1000,
      });
      return response;
    } catch (err) {
      // Asked again too soon: the server says how long to wait
      const resendInSeconds = apiErrorBody(err).resend_in;
      const current = store.sent;
      if (resendInSeconds && current) {
        store.setSent({ ...current, resendAt: Date.now() + resendInSeconds * 1000 });
      }
      throw err;
    } finally {
      setNow(Date.now());
      setIsSending(false);
    }
  };

  /** True when the code matched; otherwise `error` says why. */
  const verify = async (code: string) => {
    setIsVerifying(true);
    setError('');
    try {
      await verifyOtp(code);
      return true;
    } catch (err) {
      setError(apiErrorMessage(err, 'Verifikasi gagal. Silakan coba lagi.'));
      return false;
    } finally {
      setIsVerifying(false);
    }
  };

  return {
    turnstileRef,
    sent,
    send,
    verify,
    resendIn,
    isSending,
    isVerifying,
    error,
    setError,
  };
};

export default usePhoneVerification;
